import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Icon,
  IconButton,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Bot, CalendarClock, Clock, Repeat, User, Users, X, type LucideIcon } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { formatTaskType, getTaskContactIcon } from '../SupportTasks/utils/formatters';
import {
  describeAnchor,
  describeDedupe,
  describeGuard,
  describeProcessHour,
  describeTrigger,
  ruleStatus,
  withRenderKeys,
  type AutomationRuleListItem,
} from './ruleDisplay';

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={2}>
        {title}
      </Text>
      <VStack align="stretch" spacing={2}>
        {children}
      </VStack>
    </Box>
  );
}

function InfoRow({
  icon,
  label,
  value,
  detail,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <HStack align="start">
      <Icon as={icon} boxSize={4} color="fg.muted" mt={0.5} />
      <Text fontSize="sm" color="fg.muted" flexShrink={0}>
        {label}
      </Text>
      <VStack align="start" spacing={0}>
        <Text fontSize="sm" fontWeight="medium">
          {value}
        </Text>
        {detail != null && (
          <Text fontSize="xs" color="fg.muted">
            {detail}
          </Text>
        )}
      </VStack>
    </HStack>
  );
}

export function AutomationRuleDrawer({
  rule,
  isOpen,
  onClose,
}: {
  rule?: AutomationRuleListItem;
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!isOpen || rule == null) {
    return null;
  }

  const status = ruleStatus(rule);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottomWidth="1px" py="3">
          <HStack justify="space-between" align="center">
            <HStack align="center">
              <Text fontSize="xl" fontWeight="semibold">
                {rule.name}
              </Text>
              <Badge
                fontSize="xs"
                fontWeight="bold"
                borderRadius="full"
                colorScheme={status.colorScheme}
                textTransform="none"
              >
                {status.label}
              </Badge>
            </HStack>
            <IconButton
              icon={<X size={iconSize.lg} />}
              aria-label={t`Close`}
              variant="ghost"
              size="sm"
              onClick={onClose}
            />
          </HStack>
        </DrawerHeader>

        <DrawerBody>
          <VStack align="stretch" spacing={6} py={4}>
            {rule.parseError != null && (
              <Alert status="error" fontSize="sm" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  <Trans>This rule fails to parse and never matches:</Trans> {rule.parseError}
                </AlertDescription>
              </Alert>
            )}

            {rule.description != null && (
              <Text fontSize="sm" color="fg.muted">
                {rule.description}
              </Text>
            )}

            <Section title={<Trans>Task</Trans>}>
              <InfoRow
                icon={getTaskContactIcon(rule.taskKind)}
                label={t`Creates`}
                value={formatTaskType(rule.taskKind, null)}
              />
              <InfoRow
                icon={rule.taskProcessor === 'AUTOMATED' ? Bot : User}
                label={t`Handled by`}
                value={rule.taskProcessor === 'AUTOMATED' ? t`Automated` : t`Manual`}
              />
              <InfoRow
                icon={Users}
                label={t`Applies to`}
                value={
                  rule.scope == null
                    ? '—'
                    : rule.scope === 'unit'
                      ? t`Each released unit of an enrolment`
                      : t`Each enrolment`
                }
              />
            </Section>

            <Section title={<Trans>Schedule</Trans>}>
              <InfoRow
                icon={CalendarClock}
                label={t`Trigger`}
                value={describeTrigger(rule)}
                detail={
                  rule.trigger?.form === 'anchorOffsets'
                    ? describeAnchor(rule.trigger.anchor)
                    : undefined
                }
              />
              {rule.processAtHourSydney != null && (
                <InfoRow
                  icon={Clock}
                  label={t`Processed from (Sydney)`}
                  value={describeProcessHour(rule.processAtHourSydney)}
                />
              )}
              <InfoRow icon={Repeat} label={t`Repeat limit`} value={describeDedupe(rule)} />
            </Section>

            <Section title={<Trans>Conditions (all must be true)</Trans>}>
              {rule.guards == null ? (
                <Text fontSize="sm" color="fg.muted">
                  <Trans>The conditions could not be read. See the error above.</Trans>
                </Text>
              ) : rule.guards.length === 0 ? (
                <Text fontSize="sm" color="fg.muted">
                  <Trans>No conditions — this rule matches the whole population.</Trans>
                </Text>
              ) : (
                <UnorderedList spacing={1} fontSize="sm" pl={1}>
                  {withRenderKeys(rule.guards).map(({ guard, key }) => (
                    <ListItem key={key}>{describeGuard(guard)}</ListItem>
                  ))}
                </UnorderedList>
              )}
            </Section>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
